import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { NaturalLanguageFiller } from '@/components/NaturalLanguageFiller';
import { AccountCombobox } from '@/components/AccountCombobox';
import { FormField, FormGrid, FormSection } from '@/components/forms/FormSection';
import { ContactDuplicateBanner } from '@/components/forms/DuplicateBanner';
import {
  DECISION_ROLES,
  PREFERRED_CHANNELS,
  SENIORITY_LEVELS,
  TIME_ZONES,
  canonicaliseUrl,
  validateEmail,
  validateLinkedinUrl,
  validatePhone,
} from '@/lib/validation';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
  preselectedAccountId?: string;
}

const LANGUAGES = ['es', 'en', 'pt'] as const;

export function CreateContactModal({
  open,
  onClose,
  onCreated,
  preselectedAccountId,
}: Props) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Required
  const [accountId, setAccountId] = useState(preselectedAccountId ?? '');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');

  // Contact
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [mobilePhone, setMobilePhone] = useState('');
  const [emailSecondary, setEmailSecondary] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');

  // Role / politics
  const [title, setTitle] = useState('');
  const [seniorityLevel, setSeniorityLevel] = useState('');
  const [decisionRole, setDecisionRole] = useState('UNKNOWN');
  const [department, setDepartment] = useState('');

  // Preferences
  const [preferredChannel, setPreferredChannel] = useState('');
  const [languagePreference, setLanguagePreference] = useState('');
  const [timeZone, setTimeZone] = useState('');
  const [birthday, setBirthday] = useState('');

  // Consent (LGPD/GDPR)
  const [doNotContact, setDoNotContact] = useState(false);
  const [emailOptIn, setEmailOptIn] = useState(true);
  const [smsOptIn, setSmsOptIn] = useState(false);
  const [whatsappOptIn, setWhatsappOptIn] = useState(false);
  const [consentSource, setConsentSource] = useState('');

  // Notes / tags
  const [tagsRaw, setTagsRaw] = useState('');
  const [notes, setNotes] = useState('');

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (preselectedAccountId) setAccountId(preselectedAccountId);
  }, [preselectedAccountId]);

  const emailErr = useMemo(() => validateEmail(email), [email]);
  const emailSecErr = useMemo(() => validateEmail(emailSecondary), [emailSecondary]);
  const phoneErr = useMemo(() => validatePhone(phone), [phone]);
  const mobileErr = useMemo(() => validatePhone(mobilePhone), [mobilePhone]);
  const linkedinErr = useMemo(() => validateLinkedinUrl(linkedinUrl), [linkedinUrl]);

  if (!open) return null;

  function reset() {
    setAccountId(preselectedAccountId ?? '');
    setFirstName(''); setLastName(''); setEmail(''); setPhone(''); setMobilePhone('');
    setEmailSecondary(''); setLinkedinUrl(''); setTitle(''); setSeniorityLevel('');
    setDecisionRole('UNKNOWN'); setDepartment(''); setPreferredChannel('');
    setLanguagePreference(''); setTimeZone(''); setBirthday('');
    setDoNotContact(false); setEmailOptIn(true); setSmsOptIn(false); setWhatsappOptIn(false);
    setConsentSource(''); setTagsRaw(''); setNotes(''); setError(null);
  }

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!accountId || !firstName.trim() || !lastName.trim()) return;
    if (emailErr || emailSecErr || phoneErr || mobileErr || linkedinErr) return;
    setSubmitting(true);
    setError(null);
    try {
      const tags = tagsRaw.split(',').map((s) => s.trim()).filter(Boolean);
      const consentCapturedAt = consentSource.trim() ? new Date().toISOString() : undefined;
      await apiClient.post('/contacts', {
        accountId,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim().toLowerCase() || undefined,
        phone: phone.trim() || undefined,
        mobilePhone: mobilePhone.trim() || undefined,
        emailSecondary: emailSecondary.trim().toLowerCase() || undefined,
        linkedinUrl: linkedinUrl.trim() ? canonicaliseUrl(linkedinUrl) : undefined,
        title: title.trim() || undefined,
        seniorityLevel: seniorityLevel || undefined,
        decisionRole: decisionRole !== 'UNKNOWN' ? decisionRole : undefined,
        department: department.trim() || undefined,
        preferredChannel: preferredChannel || undefined,
        languagePreference: languagePreference || undefined,
        timeZone: timeZone || undefined,
        birthday: birthday || undefined,
        doNotContact,
        emailOptIn,
        smsOptIn,
        whatsappOptIn,
        consentSource: consentSource.trim() || undefined,
        consentCapturedAt,
        tags: tags.length > 0 ? tags : undefined,
        notes: notes.trim() || undefined,
      });
      reset();
      onCreated();
      onClose();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : t('createContact.error.generic');
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  }

  const canSubmit = !submitting && accountId && firstName.trim() && lastName.trim() &&
    !emailErr && !emailSecErr && !phoneErr && !mobileErr && !linkedinErr;

  function applyAiFill(values: Record<string, string>) {
    if (values.firstName !== undefined) setFirstName(values.firstName);
    if (values.lastName !== undefined) setLastName(values.lastName);
    if (values.email !== undefined) setEmail(values.email);
    if (values.phone !== undefined) setPhone(values.phone);
    if (values.title !== undefined) setTitle(values.title);
    if (values.seniorityLevel !== undefined) {
      const upper = values.seniorityLevel.toUpperCase();
      if ((SENIORITY_LEVELS as readonly string[]).includes(upper)) setSeniorityLevel(upper);
    }
    if (values.department !== undefined) setDepartment(values.department);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-lg border bg-card p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">{t('createContact.title')}</h3>
          <NaturalLanguageFiller
            fieldSchema={[
              { name: 'firstName', label: 'First name', type: 'text', required: true },
              { name: 'lastName', label: 'Last name', type: 'text', required: true },
              { name: 'title', label: 'Title', type: 'text' },
              { name: 'email', label: 'Email', type: 'email' },
              { name: 'phone', label: 'Phone', type: 'phone' },
              { name: 'seniorityLevel', label: 'Seniority', type: 'text' },
              { name: 'department', label: 'Department', type: 'text' },
            ]}
            currentValues={{ firstName, lastName, title, email, phone }}
            onApply={applyAiFill}
          />
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Identity */}
          <FormSection title={t('createContact.section.identity')} defaultOpen>
            <FormField label={t('createContact.field.account')} required>
              <AccountCombobox
                value={accountId || null}
                onChange={(id) => setAccountId(id ?? '')}
                disabled={Boolean(preselectedAccountId)}
              />
            </FormField>
            <FormGrid>
              <FormField label={t('createContact.field.firstName')} required>
                <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} required autoFocus />
              </FormField>
              <FormField label={t('createContact.field.lastName')} required>
                <Input value={lastName} onChange={(e) => setLastName(e.target.value)} required />
              </FormField>
            </FormGrid>
            <FormField label={t('createContact.field.email')} error={emailErr ? t(emailErr) : null}>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
            </FormField>
            {accountId && email && (
              <ContactDuplicateBanner
                email={email}
                accountId={accountId}
                onOpen={(id) => {
                  onClose();
                  navigate(`/contacts/${id}`);
                }}
              />
            )}
          </FormSection>

          {/* Role + politics */}
          <FormSection title={t('createContact.section.role')} defaultOpen>
            <FormGrid>
              <FormField label={t('createContact.field.title')}>
                <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="VP Sales" />
              </FormField>
              <FormField label={t('createContact.field.department')}>
                <Input value={department} onChange={(e) => setDepartment(e.target.value)} placeholder="Sales / IT / Finance …" />
              </FormField>
            </FormGrid>
            <FormGrid>
              <FormField label={t('createContact.field.seniorityLevel')}>
                <select
                  value={seniorityLevel}
                  onChange={(e) => setSeniorityLevel(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  <option value="">—</option>
                  {SENIORITY_LEVELS.map((s) => (
                    <option key={s} value={s}>
                      {t(`reference.seniority.${s}`)}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField
                label={t('createContact.field.decisionRole')}
                hint={t('createContact.hint.decisionRole')}
              >
                <select
                  value={decisionRole}
                  onChange={(e) => setDecisionRole(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  {DECISION_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {t(`reference.decisionRole.${r}`)}
                    </option>
                  ))}
                </select>
              </FormField>
            </FormGrid>
          </FormSection>

          {/* Channels + consent (mandatory by law for EU/BR/CO contacts) */}
          <FormSection title={t('createContact.section.consent')} defaultOpen>
            <FormGrid>
              <FormField label={t('createContact.field.phone')} error={phoneErr ? t(phoneErr) : null}>
                <Input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="+57 …" />
              </FormField>
              <FormField label={t('createContact.field.mobilePhone')} error={mobileErr ? t(mobileErr) : null}>
                <Input value={mobilePhone} onChange={(e) => setMobilePhone(e.target.value)} placeholder="+57 3…" />
              </FormField>
            </FormGrid>
            <FormField label={t('createContact.field.preferredChannel')}>
              <select
                value={preferredChannel}
                onChange={(e) => setPreferredChannel(e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              >
                <option value="">—</option>
                {PREFERRED_CHANNELS.map((c) => (
                  <option key={c} value={c}>
                    {t(`reference.preferredChannel.${c}`)}
                  </option>
                ))}
              </select>
            </FormField>

            <div className="rounded-md border border-border bg-muted/20 p-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {t('createContact.consent.matrix')}
              </p>
              <label className="mb-2 flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={doNotContact}
                  onChange={(e) => setDoNotContact(e.target.checked)}
                />
                <span>{t('createContact.field.doNotContact')}</span>
              </label>
              <FormGrid cols={3}>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={emailOptIn}
                    onChange={(e) => setEmailOptIn(e.target.checked)}
                    disabled={doNotContact}
                  />
                  <span>{t('createContact.field.emailOptIn')}</span>
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={smsOptIn}
                    onChange={(e) => setSmsOptIn(e.target.checked)}
                    disabled={doNotContact}
                  />
                  <span>{t('createContact.field.smsOptIn')}</span>
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={whatsappOptIn}
                    onChange={(e) => setWhatsappOptIn(e.target.checked)}
                    disabled={doNotContact}
                  />
                  <span>{t('createContact.field.whatsappOptIn')}</span>
                </label>
              </FormGrid>
              <div className="mt-3">
                <FormField
                  label={t('createContact.field.consentSource')}
                  hint={t('createContact.hint.consentSource')}
                >
                  <Input
                    value={consentSource}
                    onChange={(e) => setConsentSource(e.target.value)}
                    placeholder="LinkedIn DM 2026-05-22"
                  />
                </FormField>
              </div>
            </div>
          </FormSection>

          {/* More: secondary contact + preferences + notes */}
          <FormSection title={t('createContact.section.more')} defaultOpen={false}>
            <FormGrid>
              <FormField label={t('createContact.field.languagePreference')}>
                <select
                  value={languagePreference}
                  onChange={(e) => setLanguagePreference(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  <option value="">—</option>
                  {LANGUAGES.map((l) => (
                    <option key={l} value={l}>
                      {l.toUpperCase()}
                    </option>
                  ))}
                </select>
              </FormField>
              <FormField label={t('createContact.field.timeZone')}>
                <select
                  value={timeZone}
                  onChange={(e) => setTimeZone(e.target.value)}
                  className="w-full rounded-md border bg-background px-3 py-2 text-sm"
                >
                  <option value="">—</option>
                  {TIME_ZONES.map((tz) => (
                    <option key={tz} value={tz}>
                      {tz}
                    </option>
                  ))}
                </select>
              </FormField>
            </FormGrid>
            <FormGrid>
              <FormField label={t('createContact.field.emailSecondary')} error={emailSecErr ? t(emailSecErr) : null}>
                <Input type="email" value={emailSecondary} onChange={(e) => setEmailSecondary(e.target.value)} />
              </FormField>
              <FormField label={t('createContact.field.birthday')}>
                <Input type="date" value={birthday} onChange={(e) => setBirthday(e.target.value)} />
              </FormField>
            </FormGrid>
            <FormField label={t('createContact.field.linkedinUrl')} error={linkedinErr ? t(linkedinErr) : null}>
              <Input value={linkedinUrl} onChange={(e) => setLinkedinUrl(e.target.value)} placeholder="linkedin.com/in/…" />
            </FormField>
            <FormField label={t('createContact.field.tags')} hint={t('createContact.hint.tags')}>
              <Input value={tagsRaw} onChange={(e) => setTagsRaw(e.target.value)} />
            </FormField>
            <FormField label={t('createContact.field.notes')}>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              />
            </FormField>
          </FormSection>

          {error && (
            <div className="rounded-md border border-red-800 bg-red-950/40 px-3 py-2 text-sm text-red-300">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={submitting}>
              {t('common.cancel')}
            </Button>
            <Button type="submit" size="sm" disabled={!canSubmit}>
              {submitting ? t('createContact.submit.creating') : t('createContact.submit.create')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
